import axios from 'axios';

import { getAuthorizationHeader } from '../utils/getAuthHeader';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8081'}`;

const url = `${API_URL}/departments`;

// fetch departments
export const fetchDepartments = async () => {
  const result = await axios.get(url, { headers: getAuthorizationHeader() });
  return result.data;
};

// delete department
export const deleteDepartment = async (id) => {
  const result = await axios.delete(`${url}/${id}`, {
    headers: getAuthorizationHeader(),
  });
  return result.data;
};

export const fetchDepartmentsByIds = async (ids) => {
  const idsArray = Array.isArray(ids) ? ids : [ids];
  const validIds = idsArray.filter((id) => typeof id === 'number');
  if (validIds.length === 0) return [];
  const result = await axios.post(`${url}/get-by-ids`, validIds, {
    headers: getAuthorizationHeader(),
  });
  return result.data;
};

// create department
export const createDepartment = async ({ name, description, image, createdBy }) => {
  const result = await axios.post(
    url,
    { name, description, image, createdBy },
    { headers: getAuthorizationHeader() }
  );
  return result.data;
};

// update department
export const updateDepartment = async ({ id, name, description, image, createdBy }) => {
  const result = await axios.put(
    `${url}/${id}`,
    { description, name, image, createdBy },
    { headers: getAuthorizationHeader() }
  );
  return result.data;
};

// get department by ID
export const getDepartmentById = async (depId) => {
  const res = await axios.get(`${url}/${depId}`, { headers: getAuthorizationHeader() });
  return res.data;
};

export const fetchAvailableMembers = async (id) => {
  const res = await axios.get(`${url}/${id}/available-members`, {
    headers: getAuthorizationHeader(),
  });
  return res.data;
};

export const fetchAvailableGroups = async () => {
  const res = await axios.get(`${url}/available-groups`, {
    headers: getAuthorizationHeader(),
  });
  return res.data;
};

export const bulkAssignMembers = async (id, data) => {
  const res = await axios.post(`${url}/${id}/members/bulk-assign`, data, {
    headers: getAuthorizationHeader(),
  });
  return res.data;
};

// Download departments as csv
export const downloadDepartments = async () => {
  const result = await axios.get(`${API_URL}/csv/download/departments`, {
    headers: getAuthorizationHeader(),
  });
  return result.data;
};

export const unassignMemberFromDepartment = async (deptId, userId) => {
  const res = await axios.delete(`${url}/${deptId}/members/${userId}`, {
    headers: getAuthorizationHeader(),
  });
  return res.data;
};

let departmentsSummaryPromise = null;

export const fetchDepartmentsSummary = async () => {
  if (!departmentsSummaryPromise) {
    departmentsSummaryPromise = axios
      .get(`${url}/summary`, {
        headers: getAuthorizationHeader(),
      })
      .then((res) => res.data)
      .catch((err) => {
        departmentsSummaryPromise = null;
        throw err;
      });
  }
  return departmentsSummaryPromise;
};
export const getDepartmentImageUrl = (id, updatedAt) => {
  const version = updatedAt ? new Date(updatedAt).getTime() : 'default';
  return `${API_URL}/departments/${id}/image?v=${version}`;
};

// Create a PENDING department access request (Called by blocked users)
export const requestDepartmentAccess = async (
  requesterDepartmentId,
  targetDepartmentId,
  requestNote
) => {
  const result = await axios.post(
    `${url}/access-requests`,
    {},
    {
      headers: getAuthorizationHeader(),
      params: { requesterDepartmentId, targetDepartmentId, requestNote },
    }
  );
  return result.data;
};

//  Fetch all PENDING access requests in bulk (Called by managers to populate their dashboard widget) ===
export const fetchPendingAccessRequests = async () => {
  const result = await axios.get(`${url}/access-requests/pending`, {
    headers: getAuthorizationHeader(),
  });
  return result.data;
};

// Process request (Approve / Reject) ===
export const processAccessRequest = async (requestId, status) => {
  const result = await axios.put(
    `${url}/access-requests/${requestId}/process`,
    {},
    {
      headers: getAuthorizationHeader(),
      params: { status },
    }
  );
  return result.data;
};

//  Update dynamic department-to-department whitelisting (100% Isolated) ===
export const updateAllowedSubmitters = async (id, allowedSenderIds) => {
  const result = await axios.put(
    `${url}/access-requests/departments/${id}/allowed-submitters`, // Points to our isolated access controller route
    allowedSenderIds, // Passes the flat list of checked IDs in the request body
    { headers: getAuthorizationHeader() }
  );
  return result.data;
};

// Fetch whitelisted department list mapped to flat AllowedSubmitterDto (Zero N+1) ===
export const fetchAllowedSubmitters = async (id) => {
  const result = await axios.get(
    `${url}/access-requests/departments/${id}/allowed-submitters`, // Points to our isolated access controller
    { headers: getAuthorizationHeader() }
  );
  return result.data;
};

// Fetch all whitelisted and historical department requests ===
export const fetchAllAccessRequests = async () => {
  const result = await axios.get(`${url}/access-requests/all`, {
    headers: getAuthorizationHeader(),
  });
  return result.data;
};

// Fetch active, approved access tokens for the user ===
export const fetchMyActiveTokens = async () => {
  const result = await axios.get(`${url}/access-requests/my-active-tokens`, {
    headers: getAuthorizationHeader(),
  });
  return result.data;
};
